import logging from '@sstdev/lib_logging';
import { contexts, hooks } from 'lib_ui-primitives';
import { constants, continuousScanBridge, dbViews, getContinuousScanTransport } from 'lib_ui-services';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { createElement as rc, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import useBlockReaderReadsOnSetupScreen from '../../../hooks/useBlockReaderReadsOnSetupScreen';
import useContinuousScanEnabled from '../../../hooks/useContinuousScanEnabled';
import useEventSink from '../../../hooks/useEventSink';
import useNetwork from '../../../hooks/useNetwork';
import { requireReactNativeRuntime } from '../../../utilities/requireReactNativeRuntime';
import UserActivatedInput from '../../_abstractComponent/UserActivatedInput';
import { useMergedReads } from '../../contextProviders/MergedReadProvider';
import ContinuousScanWrapper from '../../list/SensorReadList/ContinuousScanWrapper';
import { getIdColumnLabel, getStagedFormMetadata, getTagPrefixForSession, resolveUseCaseForContinuousScan } from '../helpers';
import { CountRow, MultiPickerTransaction, OfflineIcon, SpreadLeftToRight, Total, TotalLabel, TotalNumber } from './styles';
const { EMPTY_ARRAY } = dbViews.emptyDbView;
const { sensorTypes } = constants;

const requireOptionalReactNative = () => {
    // Only attempt to load react-native in actual React Native environments
    if (typeof __SST_REACT_NATIVE__ === 'undefined' || !__SST_REACT_NATIVE__) {
        throw new Error('react-native loader: not in React Native context');
    }
    return requireReactNativeRuntime();
};

// combination of a "manual input" sensor (for typing or camera barcode scanning)
// on top of a SensorReadList.
const _p = {
    useMergedReads,
    useNetwork,
    useContinuousScanEnabled,
    SelectedRecordList: ContinuousScanWrapper,

    /**
     *
     * @returns {boolean}
     */
    isNative: () => __SST_REACT_NATIVE__ || __TEST_RFID__,

    // require('react-native') genuinely throws outside a real RN bundle (confirmed in this
    // package's own Node test environment) - swappable so tests can substitute a fake
    // NativeModules/NativeEventEmitter instead of only ever exercising the catch-and-return-null
    // path.
    requireReactNative: () => requireOptionalReactNative(),

    // Routed through a swappable reference so tests can substitute a fake. The one seam for the
    // whole broker-connection block — credentials, normalized tenant, group and URL — because all
    // three components that start a scan must derive it identically; see getContinuousScanTransport.
    getContinuousScanTransport: (...args) => getContinuousScanTransport(...args)
};

export const _private = _p;
export default function MultiPickerTransactionGrid(props) {
    const {
        currentRoute,
        inputAutoFocus = false,
        hNode: {
            id,
            // namespace, //hardcoded to 'sensor' in blockly
            // relation, //hardcoded to 'read' in blockly
            title,
            readOnly,
            tooltip,
            displayShowUnknownTags,
            textForShowUnknownTagsCheckbox,
            children,
            displayScanButton,
            displayRfidPowerButton,
            scanRfid,
            scanBarcode,
            includeCheckboxColumn,
            allowCheckUnknownTags = false,
            treePosition,
            variant: hVariant,
            buttonStyle: hButtonStyle,
            size
        }
    } = props || { hNode: {} };
    const [inputValue, setInputValue] = useState('');
    const [subscribe, publish, request] = useEventSink();
    const { subscribeToChange, mergeNamespace, mergeRelation } = _p.useMergedReads();
    const [entries, setEntries] = useState(EMPTY_ARRAY);
    const theme = useTheme();
    const { isOnline, statusResolved } = _p.useNetwork();
    const router = hooks.useRouter();
    const [offlineTimestamp, setOfflineTimestamp] = useState(null);
    const hasAutoNavigatedToContinuousScanRef = useRef(false);

    // tenantId/userId/formMetadata for the SessionStart this component starts itself (mirrors
    // MarkRecord/index.js). formContext.newRecord is the live Transaction form record - the same
    // record willCreate_namespace_relation_transaction.js reads server-side for the existing
    // one-tag-at-a-time submit, so the field changes configured via DynamicFieldEntry are already
    // sitting there before the user ever starts scanning.
    const sessionContext = useContext(contexts.SessionContext);
    const formContext = useContext(contexts.FormContext);
    const tenantId =
        sessionContext?.tenant?.['identity:tenant']?._id ??
        sessionContext?.tenant?._id ??
        sessionContext?.profile?.tenant?.[0]?.['identity:tenant']?._id ??
        sessionContext?.profile?.tenant?.[0]?._id;
    const userId = sessionContext?.profile?.userId ?? sessionContext?.profile?._id ?? sessionContext?.user?._id;
    const userDisplayName =
        sessionContext?.profile?.displayName ?? sessionContext?.profile?.name ?? sessionContext?.user?.displayName ?? 'User';
    // Server-side SessionStart authorization requires useCase._id to match a real use case
    // assigned to the user's tenant - the literal string 'mass-update' can never match a Mongo
    // ObjectId, so this must derive a real id. resolveUseCaseForContinuousScan always returns an
    // object (falls back to Mass Update, not Physical Inventory - unlike MarkRecord, this
    // component has no meaningful "physical inventory" default), so activeUseCase.title below
    // never needs an optional-chain guard.
    const activeUseCase = resolveUseCaseForContinuousScan(sessionContext);
    const useCaseId = activeUseCase._id;
    const useCaseTitle = activeUseCase.title;

    /** @type {[string|null, (id: string|null) => void]} */
    const [continuousScanSessionId, setContinuousScanSessionId] = useState(/** @type {string|null} */(null));
    // Set right before navigating, so the abort-on-unmount effect below doesn't cancel the very
    // session it just handed off - this component unmounts as part of that route change.
    const handingOffSessionRef = useRef(false);

    const getNativeModule = useCallback(() => {
        try {
            if (typeof __SST_REACT_NATIVE__ !== 'undefined' && __SST_REACT_NATIVE__) {
                const { NativeModules } = _p.requireReactNative();
                return NativeModules;
            }
        } catch {
            logging.warn('[MultiPickerTransactionGrid] NativeModules not available');
        }
        return null;
    }, []);

    useEffect(() => {
        if (statusResolved && !isOnline) {
            setOfflineTimestamp(prev => prev ?? new Date().toLocaleString());
        } else {
            setOfflineTimestamp(null);
        }
    }, [isOnline, statusResolved]);

    useEffect(() => {
        if (subscribeToChange != null) {
            return subscribeToChange(newEntries => setEntries(newEntries));
        }
    }, [subscribeToChange]);

    // Check if continuous scan is enabled via tenant settings. Continuous scan's PRD scope
    // (docs/product/prds/continuous-scan/continuous-scan.md §3) is "physical inventory and mass
    // update" only - this component is also reused for Check In/Check Out screens (same
    // component, different hNode config), which must NOT get continuous scan just because the
    // tenant-wide setting is on. hNode.title is a per-field label (e.g. "Assets to Check Out"),
    // not the screen identity, so it can't be used to tell screens apart - the route path can:
    // LayoutTitle.js derives the app-bar title the same way (last path segment of the route),
    // confirmed on-device as '/g/0/Mass Update'.
    const currentScreenName = currentRoute?.split('/').filter(Boolean).pop();
    // Derived rather than stored in state via an effect: the tenant setting arrives asynchronously
    // (see useContinuousScanEnabled), and an effect keyed on [scanRfid, currentScreenName] - both
    // constant for the life of the mount - would sample it once and latch the legacy screen for
    // anyone who opened Mass Update before the sync landed.
    const continuousScanTenantSetting = _p.useContinuousScanEnabled();
    const isContinuousScanEnabled = !!(scanRfid && currentScreenName === 'Mass Update' && _p.isNative() && continuousScanTenantSetting);

    useBlockReaderReadsOnSetupScreen(isContinuousScanEnabled, subscribe, publish);

    // Starting the session is deferred until the user actually initiates scanning (tap, RFID
    // click, or trigger-pull) rather than as soon as continuous scan becomes available. Unlike
    // MarkRecord's locationId (picked once, rarely changed), the user keeps editing
    // DynamicFieldEntry field values right up until they start scanning - formMetadata is only
    // ever sent once at SessionStart and never refreshed, and ContinuousScanModule.startSession
    // returns the existing session (without updating formMetadata) once one is already active.
    // Starting eagerly on mount would permanently lock in whatever fields were empty at that
    // first instant, well before the user finished configuring them.
    const startingSessionRef = useRef(false);
    /**
     * @param {object | null} [formMetadata] - the change-set resolved by the caller, so the payload
     * sent to the server is the same one the caller already gated the hand-off on.
     */
    const startContinuousScanSession = useCallback(async (formMetadata) => {
        if (continuousScanSessionId || startingSessionRef.current) {
            return continuousScanSessionId;
        }
        if (!tenantId || !userId) return null;
        const NativeModules = getNativeModule();
        if (!NativeModules?.ContinuousScanModule?.startSession) return null;

        startingSessionRef.current = true;
        try {
            const tagPrefix = await getTagPrefixForSession(request);

            // Broker URL, release group and credentials all come from one place — including the
            // normalized tenantId, which the native client builds its topics from and silo/security
            // derives back out of the username. See getContinuousScanTransport for why this is not
            // assembled here: the `.native.js` twin of this file is what Metro loads on the device,
            // and deriving it per component is how the two kept diverging.
            const transport = await _p.getContinuousScanTransport({ tenantId });
            const sessionConfig = {
                ...transport,
                userId,
                userDisplayName,
                useCaseId,
                useCaseTitle,
                streamType: 'massUpdate',
                formMetadataJson: formMetadata ? JSON.stringify(formMetadata) : undefined,
                tagPrefix
            };
            const result = await continuousScanBridge.startContinuousScan(sessionConfig);
            const newSessionId = result?.sessionId ?? null;
            setContinuousScanSessionId(newSessionId);
            return newSessionId;
        } catch (err) {
            logging.error('[MultiPickerTransactionGrid] Failed to start continuous scan session:', err);
            return null;
        } finally {
            startingSessionRef.current = false;
        }
    }, [continuousScanSessionId, tenantId, userId, userDisplayName, activeUseCase, getNativeModule, request]);

    // Abort the session if this component unmounts without handing it off (e.g. user navigates
    // away before scanning) - closes the MQTT connection and returns RFID to normal mode.
    useEffect(() => {
        return () => {
            if (continuousScanSessionId && !handingOffSessionRef.current) {
                continuousScanBridge
                    .cancelContinuousScan({ sessionId: continuousScanSessionId, reason: 'multipickertransactiongrid-unmounted' })
                    .catch(err => {
                        logging.warn('[MultiPickerTransactionGrid] Error aborting session:', err);
                    });
            }
        };
    }, [continuousScanSessionId]);

    const handleOpenContinuousScan = useCallback(async () => {
        // Mirrors doingValidate_namespace_relation_transaction.js's empty-change-set check - that
        // rule only guards the Save FormButton's submit, but this is a second, earlier way to
        // "finish" the same Mass Update form (RFID trigger pull, RFID button tap, or focusing the
        // scan input all short-circuit straight into the Active Count route via history.push,
        // bypassing the formMachine's submit pipeline entirely). Without this, a user could scan a
        // pile of tags on a form with nothing staged to update and only find out it did nothing
        // after coming back from Screen B.
        //
        // Resolved once here and handed to startContinuousScanSession, so the change-set this gate
        // accepts is byte-for-byte the one the session is started with. Reading the form twice
        // would let a value restored (or cleared) between the two reads pass the gate and then not
        // be sent.
        const formMetadata = await getStagedFormMetadata(formContext);
        const hasChanges = Object.keys(formMetadata ?? {}).length > 0;
        if (!hasChanges) {
            // Let the same interaction (focus/hover/trigger-pull) retry once the user has staged a
            // value - otherwise the "already navigated once" guards below would swallow it forever.
            hasAutoNavigatedToContinuousScanRef.current = false;
            publish(
                {
                    message: 'Select a value to update before scanning tags.',
                    type: constants.notificationTypes.TOAST,
                    variant: 'error',
                    style: { backgroundColor: 'transparent' },
                    textStyle: { fontSize: 20, color: theme.colorScheme.error }
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
            return;
        }
        // Pass along the session initiated above, so Screen B doesn't open a second,
        // redundant MQTT session.
        handingOffSessionRef.current = true;
        const sessionId = await startContinuousScanSession(formMetadata);
        // useNavigation.js blocks every history.push and replays it through the rules engine's
        // navigate_application_route workflow, which only carries the path/search through
        // (goToLocation calls history.push(newLocation) with no second argument) - any state
        // passed as history.push(path, state) is silently dropped. Query params survive that
        // replay, so scan params are passed there instead of as router state.
        const query = queryString.stringify({
            title: title || activeUseCase.title,
            streamType: 'massUpdate',
            sessionId: sessionId ?? '',
            // Resolved here rather than on Screen D, which has no hNode to read it from - see
            // getIdColumnLabel. Screen D itself is native-only, but the query string is kept
            // identical across the two copies of this component so the seam can't drift.
            idLabel: getIdColumnLabel({ children }, mergeNamespace, mergeRelation)
        });
        router.history.push(`/continuous-scan?${query}`);
    }, [router, startContinuousScanSession, title, activeUseCase, formContext, publish, children, mergeNamespace, mergeRelation, theme]);

    // Keep the ref in sync so stableHandleOpenContinuousScan always calls the latest version.
    const handleOpenContinuousScanRef = useRef(null);
    const stableHandleOpenContinuousScan = useCallback(() => handleOpenContinuousScanRef.current?.(), []);
    useEffect(() => { handleOpenContinuousScanRef.current = handleOpenContinuousScan; }, [handleOpenContinuousScan]);

    // Auto-navigate to the Active Count screen as soon as the RFID trigger is pulled on this
    // setup screen - no tap into the Scan/Enter a Tag input required. Mirrors the same listener
    // in MarkRecord/index.js: ContinuousScanModule.onTriggerPressed emits continuousScanResumed
    // on every trigger press unconditionally, regardless of whether a backend session is active.
    useEffect(() => {
        if (!isContinuousScanEnabled) return undefined;
        let NativeModules, NativeEventEmitter;
        try {
            ({ NativeModules, NativeEventEmitter } = _p.requireReactNative());
        } catch {
            return undefined;
        }
        const ContinuousScanModule = NativeModules?.ContinuousScanModule;
        if (!ContinuousScanModule) return undefined;

        const emitter = new NativeEventEmitter(ContinuousScanModule);
        const subscription = emitter.addListener('continuousScanResumed', () => {
            if (hasAutoNavigatedToContinuousScanRef.current) return;
            hasAutoNavigatedToContinuousScanRef.current = true;
            stableHandleOpenContinuousScan();
        });

        // Reset session state once the session ends (submit/finish/cancel/error) - otherwise
        // continuousScanSessionId keeps pointing at a session the server has already torn down,
        // and hasAutoNavigatedToContinuousScanRef stays permanently tripped so the next trigger
        // pull silently re-opens the dead session instead of starting a fresh one.
        const sessionEventSubscription = emitter.addListener('continuousScanSessionEvent', (event) => {
            if (['submitted', 'finished', 'cancelled', 'error'].includes(event?.type)) {
                setContinuousScanSessionId(null);
                hasAutoNavigatedToContinuousScanRef.current = false;
            }
        });

        return () => {
            subscription.remove();
            sessionEventSubscription.remove();
        };
    }, [isContinuousScanEnabled, stableHandleOpenContinuousScan]);

    const isGl2025 = theme.selectedTheme === 'gl2025';
    const variant = isGl2025 ? hVariant || 'tertiary' : 'primary';
    const buttonStyle = isGl2025 ? hButtonStyle || 'tertiary' : 'round';
    const isNative = _p.isNative();
    const inputProps = {
        id: id + 'input',
        disabled: readOnly,
        currentRoute,
        hNode: useMemo(
            () => ({ title, displayScanButton, displayRfidPowerButton, scanRfid, scanBarcode, treePosition }),
            [title, displayScanButton, displayRfidPowerButton, scanRfid, scanBarcode, treePosition]
        ),
        currentValue: inputValue,
        onChange: setInputValue,
        onRfidButtonClick: isContinuousScanEnabled ? handleOpenContinuousScan : undefined,
        forceShowRfidButton: isContinuousScanEnabled,
        tooltip,
        placeholder: 'Scan or Enter a Tag',
        autoFocus: inputAutoFocus,
        name: `${title ?? 'multi-picker-transaction-grid'}-input`
    };

    const listProperties = useMemo(() => {
        const removeButtonColumn = !includeCheckboxColumn && {
            id: `remove${id}`,
            hNodeType: 'button',
            hNodeTypeGroup: 'listColumn',
            dataType: 'button',
            title: isGl2025 ? '' : 'Remove',
            label: 'Remove',
            namespace: 'sensor',
            relation: 'read',
            iconName: 'clear',
            forAction: 'remove',
            displayTitleOnButton: false,
            variant,
            buttonStyle,
            size
        };

        return {
            currentRoute,
            includeReadButton: false,
            style: isNative ? { flexGrow: 1 } : { minHeight: '300px', flexGrow: 1 },
            hNode: {
                id: id + 'list',
                hNodeType: 'SensorReadList',
                hNodeTypeGroup: 'list',
                namespace: 'sensor',
                relation: 'read',
                sensorType:
                    isNative && scanRfid ? sensorTypes.RFID : scanBarcode ? sensorTypes.BARCODE : sensorTypes.MANUAL,
                mergeNamespace,
                mergeRelation,
                children: [removeButtonColumn, ...children],
                displayShowUnknownTags,
                textForShowUnknownTagsCheckbox,
                includeCheckbox: includeCheckboxColumn,
                includeReadButton: false, // Read button is on UserActivatedInput instead
                allowCheckUnknownTags,
                offlineText: offlineTimestamp ? `Device Connection Lost: ${offlineTimestamp}` : undefined
            }
        };
    }, [
        offlineTimestamp,
        includeCheckboxColumn,
        id,
        currentRoute,
        scanRfid,
        scanBarcode,
        mergeNamespace,
        mergeRelation,
        children,
        displayShowUnknownTags,
        textForShowUnknownTagsCheckbox,
        allowCheckUnknownTags,
        isNative,
        variant,
        buttonStyle,
        size,
        isGl2025
    ]);

    return rc(MultiPickerTransaction, { 'data-testid': props['data-testid'] },
        rc(SpreadLeftToRight, null,
            rc(UserActivatedInput, inputProps),
            rc(Total, null,
                rc(TotalLabel, null, 'Count'),
                rc(CountRow, null,
                    statusResolved && !isOnline
                        ? rc(OfflineIcon)
                        : rc(TotalNumber, null, `${entries.length || 0}`)
                )
            )
        ),
        rc(_p.SelectedRecordList, listProperties)
    );
}

MultiPickerTransactionGrid.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired
};
